import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import {
  type DragEvent as ReactDragEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Check,
  Mic,
  Pause,
  Square,
  Timer,
  Volume2,
} from 'lucide-react'
import type {
  LearningAnswerOption,
  LearningContentItem,
  LearningQuestionItem,
  LearningWorkbookChapter,
} from '@services/firebase/learning'
import { useDashboardState } from '@features/dashboard'
import { selectPreferredLearningAudio } from '@shared/media/audio/learning-audio-preference'
import {
  getAnswerOptionLabel,
  resolveLocalizedDisplayText,
  resolveLocalizedMapText,
  resolveReaderLanguage,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import { playLearningFeedbackAudio, useWarmLearningFeedbackAudio } from '@features/learning/shared/learning-feedback-audio'
import {
  getLegacySelfStudyTemplateContract,
  resolveLegacySelfStudyTemplate,
  type LegacySelfStudyTemplateId,
} from './self-study-template-contract'
import { renderRegisteredSelfStudyTemplate } from './template-registry'
import {
  LearningTemplateCompletionConfetti as LegacySelfStudyCompletionConfetti,
  LearningTemplateExerciseResultBar as LegacyExerciseResultBar,
  LearningTemplateIncorrectNote as LegacyIncorrectNote,
  LearningTemplateContinueActions as TemplateContinueActions,
  LearningTemplateIntroduction,
  LearningTemplatePromptMedia,
  LearningTemplateQuestionContent,
  LearningTemplateResultBar as LegacyTemplateResultBar,
} from '@features/learning/shared/templates'
import { ConversationTemplate } from './templates/conversation-template'
import { LegacySpeechRecoveryNotice } from './templates/speech-recovery-notice'
import { useAudioRecorder } from '@shared/media/recording/useAudioRecorder'
import { useSpeechTranscriber } from '@shared/media/speech/useSpeechTranscriber'
import { useTranslation } from 'react-i18next'
import { LmsButton, LmsInput, LmsTextarea, SafeHtml } from '@shared/ui'
import {
  LearningQuizTemplate,
  resolveLearningVocabularyTitle as resolveVocabularyTitle,
  type LearningTemplateResult,
} from '@features/learning/shared/templates'
import { getNumberedChoiceShortcutIndex } from './renderer/self-study-shortcuts'
import { MediaBlock } from './templates/media-block'
import { LegacyYoutubePlayer } from './templates/legacy-youtube-player'
import { getLegacyNumericField } from './templates/legacy-youtube-utils'
import {
  comparePracticeAnswer,
  createLegacyMatchChoices,
  createLegacyMatchTargets,
  formatElapsedDuration,
  hasScoredAnswers,
  isGenericAnswerPlaceholder,
  matchesLegacySpeechAnswer,
  matchesLegacyWriteAnswer,
  normalizeAnswerOptions,
  resolveAnswerResult,
  resolveConversationText,
  resolveLegacyAnswerOptionPayloadLabel,
  resolveLegacyCorrectAnswerReviewText,
  resolveLegacyCorrectAnswerReviewTranslationText,
  resolveLegacyMatchPair,
  shuffleTokens,
  type LegacyMatchChoice,
  type LegacyMatchTarget,
} from './templates/answer-helpers'

interface SelfStudyIntroductionSlide {
  readonly kind: 'introduction'
}

interface SelfStudyImplementationSlide {
  readonly kind: 'implementation'
}

interface SelfStudyContentSlide {
  readonly item: LearningContentItem
  readonly kind: 'content'
}

export type SelfStudyContinueResult = LearningTemplateResult
type SelfStudyScoredResult = Exclude<SelfStudyContinueResult, 'skipped'>

export type SelfStudySlide =
  | SelfStudyIntroductionSlide
  | SelfStudyImplementationSlide
  | SelfStudyContentSlide

function HtmlBlock({ html }: { readonly html: string }) {
  return <SafeHtml className="legacySelfStudyAuthoredHtml" html={html} />
}

function LegacyAuthoredContent({ html }: { readonly html: string }) {
  return (
    <div className="boxWrapQuestion legacySelfStudyQuestionWrap legacySelfStudyAuthoredContent">
      <HtmlBlock html={html} />
    </div>
  )
}

function renderQuestionText(question: LearningQuestionItem, locale: string) {
  return resolveLocalizedMapText(question.translate, locale) ?? question.text ?? ''
}

function resolveLegacyBoxQuestion(item: LearningContentItem, locale: string) {
  return resolveLocalizedDisplayText(item.question, locale) ?? null
}

function resolveItemQuestion(item: LearningContentItem, locale: string) {
  const legacyQuestion = resolveLegacyBoxQuestion(item, locale)

  if (legacyQuestion) {
    return legacyQuestion
  }

  return (item.questions ?? [])
    .filter((question) => question.status !== false)
    .map((question) => renderQuestionText(question, locale))
    .filter(Boolean)
    .join('\n')
}

function usesLegacyBoxQuestionContract(
  template: LegacySelfStudyTemplateId | null,
) {
  return (
    template === '1' ||
    template === '2' ||
    template === '3' ||
    template === '4' ||
    template === '5' ||
    template === '6' ||
    template === '7' ||
    template === '10' ||
    template === '13'
  )
}

function LegacyQuestionContent({
  includeQuestionListFallback = true,
  item,
  locale,
}: {
  readonly includeQuestionListFallback?: boolean
  readonly item: LearningContentItem
  readonly locale: string
}) {
  const question = includeQuestionListFallback
    ? resolveItemQuestion(item, locale)
    : resolveLegacyBoxQuestion(item, locale)

  return (
    <div className="boxWrapQuestion legacySelfStudyQuestionWrap">
      {item.youtubeId ? (
        <LegacyYoutubePlayer
          endAt={getLegacyNumericField(item, ['endAt', 'end_at', 'end'])}
          startAt={getLegacyNumericField(item, ['startAt', 'start_at', 'start'])}
          youtubeId={item.youtubeId}
        />
      ) : null}
      <LearningTemplatePromptMedia
        audio={item.audio ?? item.maleAudio}
        audioAutoPlayKey={item.id}
        audioAutoPlayOnLoad
        className="legacySelfStudyQuestionMedia"
        image={item.image ?? item.featuredImage ?? item.avatar}
      />
      {question ? (
        <LearningTemplateQuestionContent
          className="legacySelfStudyQuestionContent"
          content={question}
        />
      ) : null}
    </div>
  )
}

export function NumericTemplateContent({
  answerReferenceLabels,
  answers,
  continuePending,
  continuePendingLabel,
  incorrectReviewText,
  isMobileOrTablet,
  item,
  locale,
  onContinue,
  template,
}: {
  readonly answerReferenceLabels: readonly string[]
  readonly answers: readonly (LearningAnswerOption | string)[]
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly incorrectReviewText: string | null
  readonly isMobileOrTablet: boolean
  readonly item: LearningContentItem
  readonly locale: string
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly template: '1' | '2' | '5' | '6' | '10'
}) {
  const isQuiz = template === '1'
  const isWrite = template === '2'
  const isFill = template === '5'
  const isMatch = template === '6'
  const isExamples = template === '10'
  const sourceOwner =
    getLegacySelfStudyTemplateContract(template)?.sourceOwner ?? `Template${template}.js`
  const matchPairs = useMemo(
    () => answers
      .map((answer) => resolveLegacyMatchPair(answer, locale))
      .filter((pair) => pair.answerLabel && pair.targetLabel),
    [answers, locale],
  )
  const initialMatchChoices = useMemo(
    () => createLegacyMatchChoices(matchPairs, item.id),
    [item.id, matchPairs],
  )
  const initialMatchTargets = useMemo(
    () => createLegacyMatchTargets(matchPairs, item.id),
    [item.id, matchPairs],
  )
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null)
  const [writtenAnswer, setWrittenAnswer] = useState('')
  const [fillValues, setFillValues] = useState<Record<number, string>>({})
  const [matchChoiceSlots, setMatchChoiceSlots] = useState<
    Array<LegacyMatchChoice | null>
  >(initialMatchChoices)
  const [matchTargets, setMatchTargets] = useState<LegacyMatchTarget[]>(
    initialMatchTargets,
  )
  const [matchFeedbackByTargetId, setMatchFeedbackByTargetId] = useState<
    Record<string, 'correct' | 'incorrect' | null>
  >({})
  const [lockedMatchTargetIds, setLockedMatchTargetIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [isMatchResolving, setIsMatchResolving] = useState(false)
  const matchRollbackTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(
    null,
  )
  const applySelfStudyResult = useCallback((nextResult: SelfStudyScoredResult) => {
    setResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }, [])
  const visibleMatchChoices = useMemo(
    () => matchChoiceSlots
      .map((choice, slotIndex) => ({ choice, slotIndex }))
      .filter((entry): entry is { choice: LegacyMatchChoice; slotIndex: number } =>
        Boolean(entry.choice),
      ),
    [matchChoiceSlots],
  )
  const fillEntries = answers.flatMap((answer, index) =>
    typeof answer === 'string' || Boolean(answer.correct ?? answer.isRight ?? answer.result)
      ? [{ answer, index }]
      : [],
  )
  const incorrectReview = useMemo(
    () => resolveLegacyCorrectAnswerReviewText(
      item,
      answers,
      locale,
      template,
      answerReferenceLabels,
    ),
    [answerReferenceLabels, answers, item, locale, template],
  )
  const incorrectReviewAuthoredNote = useMemo(
    () => resolveLocalizedMapText(item.note, locale),
    [item.note, locale],
  )
  const incorrectReviewTranslation = useMemo(
    () => resolveLegacyCorrectAnswerReviewTranslationText(
      item,
      answers,
      locale,
      template,
    ),
    [answers, item, locale, template],
  )
  const normalizedQuizAnswers = useMemo(
    () => answers.map((answer) => {
      const answerAudio = typeof answer !== 'string' ? answer.audio : undefined
      const answerImage = typeof answer !== 'string' ? answer.image : undefined
      const rawLabel = resolveLegacyAnswerOptionPayloadLabel(answer, locale)
      const label = resolveLegacyAnswerOptionPayloadLabel(answer, locale, {
        suppressGenericPlaceholder: !answerAudio,
      })

      return {
        audio: answerAudio,
        id: typeof answer !== 'string' ? answer.id : undefined,
        image: answerImage,
        isCorrect: resolveAnswerResult(answer),
        isSparse: !label && !answerImage,
        label: label || null,
        suppressGenericLabel: Boolean(rawLabel) && !label && isGenericAnswerPlaceholder(rawLabel),
      }
    }),
    [answers, locale],
  )

  const checkWriteResult = useCallback(() => {
    applySelfStudyResult(
      matchesLegacyWriteAnswer(writtenAnswer, answerReferenceLabels)
        ? 'correct'
        : 'incorrect',
    )
  }, [answerReferenceLabels, applySelfStudyResult, writtenAnswer])

  const isFillSubmissionReady =
    fillEntries.length > 0 && fillEntries.every((entry) => Boolean(fillValues[entry.index]))

  const checkFillResult = useCallback(() => {
    const isCorrect = fillEntries.every((entry) =>
      fillValues[entry.index] === getAnswerOptionLabel(entry.answer, locale),
    )
    applySelfStudyResult(isCorrect ? 'correct' : 'incorrect')
  }, [applySelfStudyResult, fillEntries, fillValues, locale])

  const handleQuizAnswer = useCallback((
    answer: LearningAnswerOption | string,
    answerIndex: number,
  ) => {
    if (result) {
      return
    }

    setSelectedAnswerIndex(answerIndex)
    applySelfStudyResult(resolveAnswerResult(answer) ? 'correct' : 'incorrect')
  }, [applySelfStudyResult, result])

  const clearMatchRollbackTimer = useCallback(() => {
    if (matchRollbackTimerRef.current !== null) {
      window.clearTimeout(matchRollbackTimerRef.current)
      matchRollbackTimerRef.current = null
    }
  }, [])

  const finalizeMatchAttempt = useCallback((
    nextTargets: readonly LegacyMatchTarget[],
  ) => {
    clearMatchRollbackTimer()

    const nextFeedbackByTargetId = Object.fromEntries(
      nextTargets.map((target) => [
        target.id,
        target.placedChoice?.label === target.answerLabel ? 'correct' : 'incorrect',
      ]),
    ) as Record<string, 'correct' | 'incorrect' | null>
    const isCorrect = nextTargets.every(
      (target) => target.placedChoice?.label === target.answerLabel,
    )

    setMatchFeedbackByTargetId(nextFeedbackByTargetId)

    if (isCorrect) {
      setLockedMatchTargetIds(new Set(nextTargets.map((target) => target.id)))
      applySelfStudyResult('correct')
      return
    }

    setLockedMatchTargetIds(
      new Set(
        nextTargets
          .filter((target) => target.placedChoice?.label === target.answerLabel)
          .map((target) => target.id),
      ),
    )
    setIsMatchResolving(false)
    applySelfStudyResult('incorrect')
  }, [applySelfStudyResult, clearMatchRollbackTimer])

  const updateMatchState = useCallback((
    nextTargets: LegacyMatchTarget[],
    nextChoiceSlots: Array<LegacyMatchChoice | null>,
  ) => {
    setMatchChoiceSlots(nextChoiceSlots)
    setMatchTargets(nextTargets)
    setMatchFeedbackByTargetId((previous) => {
      const nextFeedback = { ...previous }
      nextTargets.forEach((target) => {
        if (!target.placedChoice || !lockedMatchTargetIds.has(target.id)) {
          nextFeedback[target.id] = null
        }
      })
      return nextFeedback
    })

    if (nextTargets.length > 0 && nextTargets.every((target) => target.placedChoice)) {
      finalizeMatchAttempt(nextTargets)
      return
    }

    if (result) {
      setResult(null)
    }
  }, [finalizeMatchAttempt, lockedMatchTargetIds, result])

  const handleMatchChoice = useCallback((slotIndex: number) => {
    if (result === 'correct' || isMatchResolving) {
      return
    }

    const choice = matchChoiceSlots[slotIndex]
    const emptyTargetIndex = matchTargets.findIndex((target) => !target.placedChoice)
    if (!choice || emptyTargetIndex === -1) {
      return
    }

    const nextChoiceSlots = matchChoiceSlots.map((slot, index) => (
      index === slotIndex ? null : slot
    ))
    const nextTargets = matchTargets.map((target, index) => (
      index === emptyTargetIndex
        ? { ...target, placedChoice: choice }
        : target
    ))

    updateMatchState(nextTargets, nextChoiceSlots)
  }, [isMatchResolving, matchChoiceSlots, matchTargets, result, updateMatchState])

  const handleRemoveMatchChoice = useCallback((targetIndex: number) => {
    if (
      result === 'correct' ||
      isMatchResolving ||
      lockedMatchTargetIds.has(matchTargets[targetIndex]?.id ?? '')
    ) {
      return
    }

    const placedChoice = matchTargets[targetIndex]?.placedChoice
    if (!placedChoice) {
      return
    }

    const nextChoiceSlots = [...matchChoiceSlots]
    const emptySlotIndex = nextChoiceSlots.findIndex((choice) => !choice)
    if (emptySlotIndex >= 0) {
      nextChoiceSlots[emptySlotIndex] = placedChoice
    }

    const nextTargets = matchTargets.map((target, index) => (
      index === targetIndex ? { ...target, placedChoice: null } : target
    ))

    updateMatchState(nextTargets, nextChoiceSlots)
  }, [
    isMatchResolving,
    lockedMatchTargetIds,
    matchChoiceSlots,
    matchTargets,
    result,
    updateMatchState,
  ])

  const handleDropOnMatchTarget = useCallback((
    event: ReactDragEvent<HTMLButtonElement>,
    targetIndex: number,
  ) => {
    event.preventDefault()

    if (
      result === 'correct' ||
      isMatchResolving ||
      lockedMatchTargetIds.has(matchTargets[targetIndex]?.id ?? '')
    ) {
      return
    }

    const [sourceKind, rawIndex] = event.dataTransfer.getData('text/plain').split(':')
    const sourceIndex = Number(rawIndex)
    if (!Number.isInteger(sourceIndex)) {
      return
    }

    const nextChoiceSlots = [...matchChoiceSlots]
    const nextTargets = matchTargets.map((target) => ({ ...target }))
    const displacedChoice = nextTargets[targetIndex]?.placedChoice ?? null

    if (sourceKind === 'choice') {
      const draggedChoice = nextChoiceSlots[sourceIndex]
      if (!draggedChoice) {
        return
      }

      nextChoiceSlots[sourceIndex] = displacedChoice
      nextTargets[targetIndex] = {
        ...nextTargets[targetIndex],
        placedChoice: draggedChoice,
      }
      updateMatchState(nextTargets, nextChoiceSlots)
      return
    }

    if (
      sourceKind === 'target' &&
      !lockedMatchTargetIds.has(matchTargets[sourceIndex]?.id ?? '')
    ) {
      const draggedChoice = nextTargets[sourceIndex]?.placedChoice ?? null
      if (!draggedChoice) {
        return
      }

      nextTargets[sourceIndex] = {
        ...nextTargets[sourceIndex],
        placedChoice: displacedChoice,
      }
      nextTargets[targetIndex] = {
        ...nextTargets[targetIndex],
        placedChoice: draggedChoice,
      }
      updateMatchState(nextTargets, nextChoiceSlots)
    }
  }, [
    isMatchResolving,
    lockedMatchTargetIds,
    matchChoiceSlots,
    matchTargets,
    result,
    updateMatchState,
  ])

  const handleDropOnMatchChoice = useCallback((
    event: ReactDragEvent<HTMLButtonElement>,
    choiceSlotIndex: number,
  ) => {
    event.preventDefault()

    if (result === 'correct' || isMatchResolving) {
      return
    }

    const [sourceKind, rawIndex] = event.dataTransfer.getData('text/plain').split(':')
    const sourceIndex = Number(rawIndex)
    if (!Number.isInteger(sourceIndex)) {
      return
    }

    const nextChoiceSlots = [...matchChoiceSlots]
    const nextTargets = matchTargets.map((target) => ({ ...target }))

    if (
      sourceKind === 'target' &&
      !lockedMatchTargetIds.has(matchTargets[sourceIndex]?.id ?? '')
    ) {
      const draggedChoice = nextTargets[sourceIndex]?.placedChoice ?? null
      if (!draggedChoice) {
        return
      }

      nextTargets[sourceIndex] = {
        ...nextTargets[sourceIndex],
        placedChoice: nextChoiceSlots[choiceSlotIndex],
      }
      nextChoiceSlots[choiceSlotIndex] = draggedChoice
      updateMatchState(nextTargets, nextChoiceSlots)
      return
    }

    if (sourceKind === 'choice') {
      const draggedChoice = nextChoiceSlots[sourceIndex]
      if (!draggedChoice) {
        return
      }

      nextChoiceSlots[sourceIndex] = nextChoiceSlots[choiceSlotIndex]
      nextChoiceSlots[choiceSlotIndex] = draggedChoice
      updateMatchState(nextTargets, nextChoiceSlots)
    }
  }, [
    isMatchResolving,
    lockedMatchTargetIds,
    matchChoiceSlots,
    matchTargets,
    result,
    updateMatchState,
  ])

  useEffect(() => clearMatchRollbackTimer, [clearMatchRollbackTimer])

  const handleExerciseKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    if (result === 'correct' || !isMatch || isMatchResolving) {
      return
    }

    const choiceIndex = getNumberedChoiceShortcutIndex(event, visibleMatchChoices.length)
    if (choiceIndex === null) {
      return
    }

    const choice = visibleMatchChoices[choiceIndex]
    if (!choice) {
      return
    }

    event.preventDefault()
    handleMatchChoice(choice.slotIndex)
  }, [
    handleMatchChoice,
    isMatch,
    isMatchResolving,
    result,
    visibleMatchChoices,
  ])

  useEffect(() => {
    window.addEventListener('keydown', handleExerciseKeyboardShortcut)

    return () => {
      window.removeEventListener('keydown', handleExerciseKeyboardShortcut)
    }
  }, [handleExerciseKeyboardShortcut])

  const renderExamples = () => (
    <div
      className="boxWrapQuestion legacySelfStudyExamples"
      data-legacy-owner={sourceOwner}
      data-legacy-template="10"
      data-qa="legacy-template-10-examples"
    >
      {(item.examples ?? []).map((example, index, examples) => {
        const title =
          resolveLocalizedMapText(example.translate, locale) ??
          resolveTranslatedText(example.translates, locale) ??
          example.text ??
          ''
        return (
          <LegacyExampleRow
            example={example}
            key={`${item.id}-example-${index}`}
            label={examples.length > 1 ? `Example ${index + 1}` : 'Example'}
            title={title}
          />
        )
      })}
    </div>
  )

  const useLegacyBoxQuestionContract = usesLegacyBoxQuestionContract(template)

  return (
    <>
      <LegacyQuestionContent
        includeQuestionListFallback={!useLegacyBoxQuestionContract}
        item={item}
        locale={locale}
      />
      {!useLegacyBoxQuestionContract && item.data?.text ? (
        <LegacyAuthoredContent html={item.data.text} />
      ) : null}

      {isQuiz ? (
        <LearningQuizTemplate
          answers={normalizedQuizAnswers}
          fullwidth={item.fullwidth}
          itemId={item.id}
          onAnswer={(answerIndex) => {
            const answer = answers[answerIndex]
            if (answer === undefined) {
              return
            }

            handleQuizAnswer(answer, answerIndex)
          }}
          oneThird={item.oneThird}
          result={result}
          selectedAnswerIndex={selectedAnswerIndex}
          sourceOwner={sourceOwner}
        />
      ) : null}

      {isWrite ? (
        <div
          className="boxWrapQuestion legacySelfStudyWriteExercise"
          data-legacy-owner={sourceOwner}
          data-legacy-template="2"
          data-qa="legacy-template-2-write"
        >
          <div className={`legacySelfStudyWriteInput ${result ?? ''}`}>
            <LmsInput
              autoComplete="off"
              autoFocus
              disabled={Boolean(result) || answerReferenceLabels.length === 0}
              onChange={(event) => setWrittenAnswer(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !result) {
                  checkWriteResult()
                }
              }}
              placeholder={answerReferenceLabels.length > 0 ? 'Write your answer' : ''}
              value={writtenAnswer}
            />
          </div>
          {!result ? (
            <div className="legacySelfStudyExerciseActions">
              <LmsButton
                disabled={answerReferenceLabels.length === 0}
                onClick={checkWriteResult}
                type="button"
                variant="accent"
              >
                Check
              </LmsButton>
            </div>
          ) : null}
        </div>
      ) : null}

      {isFill ? (
        <div
          className="boxWrapQuestion legacySelfStudyCompleteExercise"
          data-legacy-owner={sourceOwner}
          data-legacy-template="5"
          data-qa="legacy-template-5-complete-sentence"
        >
          <div>
            {answers.map((answer, index) => {
              const label = getAnswerOptionLabel(answer, locale)
              const isBlank = typeof answer === 'string' || resolveAnswerResult(answer)
              const value = fillValues[index] ?? ''
              return isBlank ? (
                <LmsInput
                  aria-label={`Answer ${index + 1}`}
                  autoComplete="off"
                  className={result ? (value === label ? 'correct' : 'incorrect') : ''}
                  disabled={Boolean(result)}
                  key={`${item.id}-fill-${index}`}
                  onChange={(event) => {
                    setFillValues((previous) => ({ ...previous, [index]: event.target.value }))
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && isFillSubmissionReady && !result) {
                      checkFillResult()
                    }
                  }}
                  style={{ width: `${Math.max(60, Math.max(value.length, label.length, 3) * 10 + 30)}px` }}
                  value={value}
                />
              ) : (
                <span key={`${item.id}-fill-${index}`}>{label} </span>
              )
            })}
          </div>
          {!result ? (
            <div className="legacySelfStudyExerciseActions">
              <LmsButton
                disabled={!isFillSubmissionReady}
                onClick={checkFillResult}
                type="button"
                variant="accent"
              >
                Check
              </LmsButton>
            </div>
          ) : null}
        </div>
      ) : null}

      {isMatch ? (
        <div
          className="boxWrapAnswer legacySelfStudyMatchColumn"
          data-legacy-owner={sourceOwner}
          data-legacy-template="6"
          data-qa="legacy-template-6-match"
        >
          <div className={`legacySelfStudyMatchTargetGrid${matchTargets.length < 4 ? ' compact' : ''}`}>
            {matchTargets.map((target, index) => {
              const placedChoice = target.placedChoice
              const feedback = matchFeedbackByTargetId[target.id] ?? null
              const isLocked = lockedMatchTargetIds.has(target.id)
              const isDropInteractionLocked = isLocked || isMatchResolving || result === 'correct'

              return (
                <div className="legacySelfStudyMatchPair" key={target.id}>
                  <div
                    className="legacySelfStudyMatchPrompt"
                    style={{ order: index + 1 }}
                  >
                    <div className="legacySelfStudyMatchText">{target.targetLabel}</div>
                  </div>
                  <LmsButton
                    aria-disabled={isDropInteractionLocked}
                    aria-label={placedChoice ? `Remove ${placedChoice.label}` : `Match answer slot ${index + 1}`}
                    className={[
                      'legacySelfStudyMatchDrop',
                      placedChoice ? 'filled' : '',
                      placedChoice && feedback ? feedback : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    draggable={Boolean(placedChoice) && !isDropInteractionLocked}
                    onDragOver={(event) => {
                      event.preventDefault()
                    }}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', `target:${index}`)
                    }}
                    onDrop={(event) => {
                      handleDropOnMatchTarget(event, index)
                    }}
                    onClick={() => {
                      handleRemoveMatchChoice(index)
                    }}
                    style={{ order: matchTargets.length + index + 1 }}
                    type="button"
                    variant="ghost"
                  >
                    {!placedChoice ? <span aria-hidden="true" className="legacySelfStudyMatchEmptyBox">
                      <svg>
                        <rect height="100%" rx="8px" ry="8px" width="100%" />
                      </svg>
                    </span> : null}
                    {placedChoice ? (
                      <span className="legacySelfStudyMatchText">{placedChoice.label}</span>
                    ) : null}
                  </LmsButton>
                </div>
              )
            })}
          </div>
          <div className={`legacySelfStudyMatchChoiceGrid${matchChoiceSlots.length < 4 ? ' compact' : ''}`}>
            {(() => {
              let visibleChoiceNumber = 0

              return matchChoiceSlots.map((choice, index) => {
                const shortcutNumber = choice ? visibleChoiceNumber + 1 : null
                const isChoiceInteractionLocked = isMatchResolving || result === 'correct'

                if (choice) {
                  visibleChoiceNumber += 1
                }

                return (
                  <LmsButton
                    aria-disabled={isChoiceInteractionLocked}
                    aria-keyshortcuts={
                      shortcutNumber && shortcutNumber < 10 && result !== 'correct'
                        ? String(shortcutNumber)
                        : undefined
                    }
                    className={!choice ? 'isSelected' : ''}
                    draggable={Boolean(choice) && !isMatchResolving && result !== 'correct'}
                    key={`${item.id}-match-choice-${index}`}
                    onDragOver={(event) => {
                      event.preventDefault()
                    }}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', `choice:${index}`)
                    }}
                    onDrop={(event) => {
                      handleDropOnMatchChoice(event, index)
                    }}
                    onClick={() => handleMatchChoice(index)}
                    tabIndex={choice ? 0 : -1}
                    type="button"
                    variant="ghost"
                  >
                    {choice ? <span className="legacySelfStudyMatchText">{choice.label}</span> : null}
                    {shortcutNumber && result !== 'correct' ? (
                      <span className="legacySelfStudyExerciseSort">{shortcutNumber}</span>
                    ) : null}
                  </LmsButton>
                )
              })
            })()}
          </div>
        </div>
      ) : null}

      {isExamples ? renderExamples() : null}
      {isExamples ? (
        <LegacyTemplateResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          finished={false}
          onNext={() => onContinue('correct')}
        />
      ) : null}
      {result === 'incorrect' ? (
        <LegacyIncorrectNote
          html={incorrectReviewAuthoredNote}
          isMobileOrTablet={isMobileOrTablet}
          text={incorrectReviewAuthoredNote ? null : incorrectReview ?? incorrectReviewText}
          translationText={incorrectReviewAuthoredNote ? null : incorrectReviewTranslation}
        />
      ) : null}
      {result ? (
        <LegacyExerciseResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          onNext={() => onContinue(result)}
          result={result}
        />
      ) : null}
    </>
  )
}

function LegacyExampleRow({
  example,
  label,
  title,
}: {
  readonly example: NonNullable<LearningContentItem['examples']>[number]
  readonly label: string
  readonly title: string
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  return (
    <div className="legacySelfStudyExampleRow">
      <div>
        <span>{label}</span>
        {example.audio ? (
          <LmsButton
            aria-label={`Play ${label}`}
            onClick={() => {
              if (audioRef.current?.paused) {
                void audioRef.current.play().catch(() => undefined)
              } else {
                audioRef.current?.pause()
              }
            }}
            type="button"
            variant="ghost"
          >
            {isPlaying ? <Pause aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
          </LmsButton>
        ) : null}
      </div>
      <div className="legacySelfStudyExampleContent">
        {example.image ? <img alt="" src={example.image} /> : null}
        <p>{title}</p>
      </div>
      {example.audio ? (
        <CachedLearningAudio
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          ref={audioRef}
          src={example.audio}
        />
      ) : null}
    </div>
  )
}

function LegacySpeechAnswerTemplate({
  continuePending,
  continuePendingLabel,
  expectedAnswers,
  incorrectReviewText,
  isMobileOrTablet,
  onContinue,
  sourceOwner,
}: {
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly expectedAnswers: readonly string[]
  readonly incorrectReviewText: string | null
  readonly isMobileOrTablet: boolean
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly sourceOwner: string
}) {
  const recorder = useAudioRecorder({ maxDurationMs: 15000 })
  const transcriber = useSpeechTranscriber({ lang: 'vi-VN' })
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)
  const recoveryMessage = transcriber.errorMessage ?? recorder.errorMessage
  const applySelfStudyResult = useCallback((nextResult: SelfStudyScoredResult) => {
    setResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }, [])

  const handleStart = async () => {
    setResult(null)
    transcriber.resetTranscript()
    recorder.clearAudioUrl()
    transcriber.startListening()
    await recorder.startRecording()
  }

  const handleStop = () => {
    transcriber.stopListening()
    recorder.stopRecording()
    applySelfStudyResult(
      matchesLegacySpeechAnswer(transcriber.transcript, expectedAnswers) ? 'correct' : 'incorrect',
    )
  }

  return (
    <>
      <div
        className="boxWrapQuestion legacySelfStudySpeechExercise"
        data-legacy-owner={sourceOwner}
        data-legacy-template="7"
        data-qa="legacy-template-7-speech"
      >
        <div className={`legacySelfStudyWriteInput ${result ?? ''}`}>
          <LmsInput
            data-qa="legacy-template-7-speech-input"
            disabled
            placeholder={transcriber.isListening ? 'Listening...' : 'Click the icon to start speaking.'}
            value={transcriber.transcript}
          />
        </div>
        <div className="legacySelfStudySpeechRecord">
          <LmsButton
            aria-label={transcriber.isListening ? 'Stop speaking' : 'Start speaking'}
            className={transcriber.isListening ? 'recording' : ''}
            data-qa="legacy-template-7-speech-record"
            onClick={() => {
              if (transcriber.isListening || recorder.isRecording) {
                handleStop()
                return
              }
              void handleStart()
            }}
            size="icon-lg"
            type="button"
            variant="accent"
          >
            {transcriber.isListening || recorder.isRecording ? <Square aria-hidden="true" /> : <Mic aria-hidden="true" />}
          </LmsButton>
          {recorder.audioUrl ? <CachedLearningAudio controls src={recorder.audioUrl} /> : null}
        </div>
        <LegacySpeechRecoveryNotice
          message={recoveryMessage}
          onRetry={() => void handleStart()}
          onSkip={() => onContinue('skipped')}
        />
      </div>
      {result === 'incorrect' ? (
        <LegacyIncorrectNote
          html={incorrectReviewText}
          isMobileOrTablet={isMobileOrTablet}
        />
      ) : null}
      {result ? (
        <LegacyExerciseResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          onNext={() => onContinue(result)}
          result={result}
        />
      ) : null}
    </>
  )
}

function SpeechPracticeTemplate({
  continuePending,
  continuePendingLabel,
  expectedAnswers,
  isMobileBlocked,
  mode,
  onContinue,
  sourceOwner,
}: {
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly expectedAnswers: readonly string[]
  readonly isMobileBlocked: boolean
  readonly mode: 'scored' | 'self-check'
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly sourceOwner: string
}) {
  const [manualAnswer, setManualAnswer] = useState('')
  const [hasFinished, setHasFinished] = useState(false)
  const [selectedResult, setSelectedResult] = useState<'correct' | 'incorrect' | null>(null)
  const recorder = useAudioRecorder({ maxDurationMs: 15000 })
  const transcriber = useSpeechTranscriber({ lang: 'vi-VN' })
  const applySelfStudyResult = useCallback((nextResult: SelfStudyScoredResult) => {
    setSelectedResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }, [])

  const currentAnswer = manualAnswer || transcriber.transcript
  const recoveryMessage = transcriber.errorMessage ?? recorder.errorMessage

  const startPractice = async () => {
    setHasFinished(false)
    setSelectedResult(null)
    setManualAnswer('')
    transcriber.resetTranscript()
    recorder.clearAudioUrl()

    if (!isMobileBlocked) {
      transcriber.startListening()
      await recorder.startRecording()
    }
  }

  const finishPractice = () => {
    transcriber.stopListening()
    recorder.stopRecording()
    setHasFinished(true)
  }

  const canCheck = Boolean(currentAnswer.trim()) || recorder.audioUrl !== null

  if (mode === 'self-check') {
    return (
      <>
        {isMobileBlocked ? (
          <div
            className="boxWrapQuestion legacySelfStudyTemplate13MobileNotice"
            data-legacy-owner={sourceOwner}
            data-legacy-template="13"
            data-qa="legacy-template-13-mobile-notice"
          >
            <p>This speaking exercise is currently not available on mobile and tablet platforms.</p>
          </div>
        ) : (
          <div
            className="boxWrapQuestion legacySelfStudySpeedToText"
            data-legacy-owner={sourceOwner}
            data-legacy-template="13"
            data-qa="legacy-template-13-speed-to-text"
          >
            <div className="legacySelfStudyWriteInput">
              <LmsInput
                disabled
                placeholder={
                  transcriber.isListening ? 'Listening...' : 'Click the icon to start speaking.'
                }
                value={transcriber.transcript}
              />
            </div>
            <div className="legacySelfStudySpeechRecord">
              <LmsButton
                aria-label={
                  transcriber.isListening || recorder.isRecording
                    ? 'Stop speaking'
                    : 'Start speaking'
                }
                className={
                  transcriber.isListening || recorder.isRecording ? 'recording' : ''
                }
                onClick={() => {
                  if (transcriber.isListening || recorder.isRecording) {
                    finishPractice()
                    return
                  }
                  void startPractice()
                }}
                size="icon-lg"
                type="button"
                variant="accent"
              >
                {transcriber.isListening || recorder.isRecording ? (
                  <Square aria-hidden="true" />
                ) : (
                  <Mic aria-hidden="true" />
                )}
              </LmsButton>
              {recorder.audioUrl ? <CachedLearningAudio controls src={recorder.audioUrl} /> : null}
            </div>
            <LegacySpeechRecoveryNotice
              message={recoveryMessage}
              onRetry={() => void startPractice()}
              onSkip={() => onContinue('skipped')}
            />
          </div>
        )}
        <LegacyTemplateResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          finished={false}
          onNext={() => onContinue('correct')}
        />
      </>
    )
  }

  return (
    <div className="selfStudyStack selfStudyStack--2">
      {isMobileBlocked ? (
        <div className="selfStudyStepCard selfStudyWarning" role="alert">
          This speaking exercise is currently not available on mobile and tablet platforms.
        </div>
      ) : null}

      {!transcriber.isSupported && !isMobileBlocked ? (
        <div className="selfStudyStepCard selfStudyInfo" role="status">
          Speech recognition is unavailable in this browser. Type what you said below to keep the legacy-compatible fallback path moving.
        </div>
      ) : null}

      {!recorder.isSupported && !isMobileBlocked ? (
        <div className="selfStudyStepCard selfStudyInfo" role="status">
          Audio recording playback is unavailable in this browser. The practice flow still works through speech-to-text or typed input.
        </div>
      ) : null}

      <LegacySpeechRecoveryNotice
        message={recoveryMessage}
        onRetry={() => void startPractice()}
        onSkip={() => onContinue('skipped')}
      />

      <div className="selfStudyStepCard selfStudyCardPadding">
        <div className="selfStudyStack selfStudyStack--2">
          <p className="selfStudyText selfStudyText--subtitle">
            Read the answer aloud, or type it if the browser blocks speech input.
          </p>
          <label className="legacySelfStudyFieldLabel" htmlFor="legacy-self-study-practice-answer">
            Captured or typed answer
          </label>
          <LmsTextarea
            id="legacy-self-study-practice-answer"
            onChange={(event) => {
              setManualAnswer(event.target.value)
              setSelectedResult(null)
            }}
            placeholder="Say the sentence aloud, or type it here when speech recognition is unavailable."
            rows={3}
            value={manualAnswer || transcriber.transcript}
          />
          {recorder.audioUrl ? <CachedLearningAudio controls src={recorder.audioUrl} /> : null}
          <div className="selfStudyStack selfStudyStack--responsive-row-1-5">
            {transcriber.isListening || recorder.isRecording ? (
              <LmsButton
                leadingIcon={<Square aria-hidden="true" />}
                onClick={finishPractice}
                variant="accent"
              >
                Finish practice
              </LmsButton>
            ) : (
              <LmsButton
                leadingIcon={<Mic aria-hidden="true" />}
                onClick={() => {
                  void startPractice()
                }}
                variant="accent"
              >
                Start practice
              </LmsButton>
            )}
            <LmsButton
              disabled={!canCheck}
              leadingIcon={<Check aria-hidden="true" />}
              onClick={() => {
                if (expectedAnswers.length === 0) {
                  applySelfStudyResult('correct')
                  return
                }

                applySelfStudyResult(
                  comparePracticeAnswer(currentAnswer, expectedAnswers)
                    ? 'correct'
                    : 'incorrect',
                )
              }}
              variant="outline"
            >
              Check practice
            </LmsButton>
          </div>
        </div>
      </div>

      {hasFinished && mode === 'scored' && selectedResult === null && canCheck ? (
        <div className="selfStudyStepCard selfStudyInfo" role="status">
          Review the captured answer, then check whether it matches the target.
        </div>
      ) : null}

      {selectedResult ? (
        <TemplateContinueActions
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          incorrectReviewText={expectedAnswers.join(' / ') || null}
          isMobileOrTablet={isMobileBlocked}
          onContinue={onContinue}
          result={selectedResult}
        />
      ) : null}
    </div>
  )
}

function SparseTemplateFallback({
  answerReferenceLabels,
  answers,
  continuePending,
  continuePendingLabel,
  incorrectReviewText,
  isMobileOrTablet,
  item,
  locale,
  onContinue,
  template,
}: {
  readonly answerReferenceLabels: string[]
  readonly answers: Array<LearningAnswerOption | string>
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly incorrectReviewText: string | null
  readonly isMobileOrTablet: boolean
  readonly item: LearningContentItem
  readonly locale: string
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly template: LegacySelfStudyTemplateId
}) {
  const [selectedResult, setSelectedResult] = useState<'correct' | 'incorrect' | null>(null)
  const [writtenAnswer, setWrittenAnswer] = useState('')
  const [fillAnswers, setFillAnswers] = useState<Record<number, string>>({})
  const [matchAssignments, setMatchAssignments] = useState<string[]>([])
  const applySelfStudyResult = useCallback((nextResult: SelfStudyScoredResult) => {
    setSelectedResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }, [])
  const scoredAnswers = hasScoredAnswers(answers)
  const isWriteTemplate = template === '2'
  const isFillTemplate = template === '5'
  const isMatchTemplate = template === '6'
  const isExamplesTemplate = template === '10'
  const matchChoices = useMemo(
    () => shuffleTokens(answerReferenceLabels),
    [answerReferenceLabels],
  )
  const sparseWriteInputId = `${item.id}-sparse-write-answer`
  const usesDedicatedAnswerSurface =
    isWriteTemplate || isFillTemplate || isMatchTemplate || isExamplesTemplate
  const showMultipleChoiceAnswers = scoredAnswers && !usesDedicatedAnswerSurface
  const showAnswerReference =
    answers.length > 0 && !showMultipleChoiceAnswers && !usesDedicatedAnswerSurface

  return (
    <div className={`selfStudyStack selfStudyStack--3 legacySelfStudyTemplateAdapter template-${template}`}>
      <LegacyQuestionContent item={item} locale={locale} />

      {item.data?.text ? <LegacyAuthoredContent html={item.data.text} /> : null}

      {item.text && !resolveItemQuestion(item, locale) ? (
        <p className="selfStudyText selfStudyText--prewrap">
          {item.text}
        </p>
      ) : null}

      {item.vocabularies && item.vocabularies.length > 0 ? (
        <section className="selfStudyStack selfStudyStack--1-25">
          <h3 className="selfStudyText selfStudyText--heading">Vocabulary</h3>
          {item.vocabularies.map((vocabulary, index) => (
            <div
              className="selfStudyStepCard selfStudyCardPaddingSmall"
              key={`${item.id}-vocabulary-${index}`}
            >
              <div className="selfStudyStack selfStudyStack--1-25">
                <p className="selfStudyText selfStudyText--subtitle">
                  {resolveVocabularyTitle(vocabulary, locale) ?? `Entry ${index + 1}`}
                </p>
                <MediaBlock
                  audio={vocabulary.audio}
                  image={vocabulary.image ?? vocabulary.featuredImage}
                  title={resolveVocabularyTitle(vocabulary, locale)}
                />
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {item.examples && item.examples.length > 0 ? (
        <section className="selfStudyStack selfStudyStack--1-25">
          <h3 className="selfStudyText selfStudyText--heading">Examples</h3>
          {item.examples.map((example, index) => (
            <div
              className="selfStudyStepCard selfStudyCardPaddingSmall"
              key={`${item.id}-example-${index}`}
            >
              <div className="selfStudyStack selfStudyStack--1">
                <p className="selfStudyText">
                  {resolveLocalizedMapText(example.translate, locale) ??
                    resolveTranslatedText(example.translates, locale) ??
                    example.text ??
                    `Example ${index + 1}`}
                </p>
                <MediaBlock
                  audio={example.audio}
                  image={example.image}
                  title={example.text}
                />
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {item.conversation && item.conversation.length > 0 ? (
        <section className="selfStudyStack selfStudyStack--1-25">
          <h3 className="selfStudyText selfStudyText--heading">Conversation</h3>
          {item.conversation
            .filter((conversation) => conversation.status !== false)
            .map((conversation, index) => (
              <div
                className="selfStudyStepCard selfStudyCardPaddingSmall"
                key={`${item.id}-conversation-${index}`}
              >
                <div className="selfStudyStack selfStudyStack--1">
                  <div className="selfStudyInlineRow selfStudyInlineRow--between">
                    <span className="selfStudyText selfStudyText--label">
                      {conversation.speaker ?? `Line ${index + 1}`}
                    </span>
                    {conversation.audio ? <span className="selfStudyChip">Audio</span> : null}
                  </div>
                  <p className="selfStudyText">
                    {resolveConversationText(conversation, locale)}
                  </p>
                  <MediaBlock audio={conversation.audio} image={conversation.image} />
                </div>
              </div>
            ))}
        </section>
      ) : null}

      {showMultipleChoiceAnswers ? (
        <div className="selfStudyStack selfStudyStack--1-5">
          <div className="selfStudyAnswerRow">
            {answers.map((answer, index) => {
              const label = resolveLegacyAnswerOptionPayloadLabel(answer, locale, {
                suppressGenericPlaceholder: true,
              })
              const answerState = selectedResult
                ? resolveAnswerResult(answer)
                  ? 'correct'
                  : 'inactive'
                : ''

              return (
                <LmsButton
                  className={`legacySelfStudySparseAnswerButton${answerState ? ` ${answerState}` : ''}`}
                  data-sparse-answer={!label ? 'true' : undefined}
                  key={`${item.id}-answer-${index}`}
                  onClick={() => {
                    applySelfStudyResult(resolveAnswerResult(answer) ? 'correct' : 'incorrect')
                  }}
                  variant="ghost"
                >
                  {label}
                </LmsButton>
              )
            })}
          </div>
        </div>
      ) : null}

      {showAnswerReference ? (
        <section className="selfStudyStack selfStudyStack--1-25">
          <h3 className="selfStudyText selfStudyText--heading">
            {answers.length === 1 ? 'Expected answer' : 'Reference answers'}
          </h3>
          {answerReferenceLabels.map((answerLabel, index) => (
            <div
              className="selfStudyStepCard selfStudyCardPaddingSmall"
              key={`${item.id}-answer-reference-${index}`}
            >
              <p className="selfStudyText">{answerLabel}</p>
            </div>
          ))}
        </section>
      ) : null}

      {isWriteTemplate ? (
        <div className="selfStudyStack selfStudyStack--2 legacySelfStudyWriteTemplate">
          <label className="legacySelfStudyFieldLabel" htmlFor={sparseWriteInputId}>
            Write your answer
          </label>
          <LmsInput
            autoFocus
            disabled={Boolean(selectedResult) || answerReferenceLabels.length === 0}
            id={sparseWriteInputId}
            onChange={(event) => {
              setWrittenAnswer(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || selectedResult) {
                return
              }

              applySelfStudyResult(
                comparePracticeAnswer(writtenAnswer, answerReferenceLabels)
                  ? 'correct'
                  : 'incorrect',
              )
            }}
            value={writtenAnswer}
          />
          {!selectedResult ? (
            <LmsButton
              disabled={answerReferenceLabels.length === 0 || !writtenAnswer.trim()}
              onClick={() => {
                applySelfStudyResult(
                  comparePracticeAnswer(writtenAnswer, answerReferenceLabels)
                    ? 'correct'
                  : 'incorrect',
                )
              }}
              variant="accent"
            >
              Check
            </LmsButton>
          ) : null}
        </div>
      ) : null}

      {isFillTemplate ? (
        <div className="selfStudyStack selfStudyStack--2 legacySelfStudyFillTemplate">
          {answerReferenceLabels.map((_, index) => {
            const inputId = `${item.id}-sparse-fill-${index}`

            return (
              <div className="legacySelfStudyFillField" key={`${item.id}-fill-${index}`}>
                <label className="legacySelfStudyFieldLabel" htmlFor={inputId}>
                  Answer {index + 1}
                </label>
                <LmsInput
                  disabled={Boolean(selectedResult)}
                  id={inputId}
                  onChange={(event) => {
                    setFillAnswers((previous) => ({ ...previous, [index]: event.target.value }))
                  }}
                  value={fillAnswers[index] ?? ''}
                />
              </div>
            )
          })}
          {!selectedResult ? (
            <LmsButton
              disabled={
                answerReferenceLabels.length === 0 ||
                answerReferenceLabels.some((_, index) => !(fillAnswers[index] ?? '').trim())
              }
              onClick={() => {
                const isCorrect = answerReferenceLabels.every((answerLabel, index) =>
                  comparePracticeAnswer(fillAnswers[index] ?? '', [answerLabel]),
                )
                applySelfStudyResult(isCorrect ? 'correct' : 'incorrect')
              }}
              variant="accent"
            >
              Check
            </LmsButton>
          ) : null}
        </div>
      ) : null}

      {isMatchTemplate ? (
        <div className="selfStudyStack selfStudyStack--2 legacySelfStudyMatchTemplate">
          <div className="selfStudyStack selfStudyStack--responsive-row-1-5">
            <div className="selfStudyStack selfStudyStack--1 selfStudyFlexOne">
              {answerReferenceLabels.map((answerLabel, index) => (
                <div className="legacySelfStudyMatchTarget" key={`${item.id}-target-${index}`}>
                  {answerLabel}
                  <span>{matchAssignments[index] ?? '—'}</span>
                </div>
              ))}
            </div>
            <div className="selfStudyStack selfStudyStack--1 selfStudyFlexOne">
              {matchChoices.map((answerLabel, index) => (
                <LmsButton
                  disabled={Boolean(selectedResult) || matchAssignments.includes(answerLabel)}
                  key={`${item.id}-choice-${index}`}
                  onClick={() => {
                    setMatchAssignments((previous) => [...previous, answerLabel])
                  }}
                  variant="outline"
                >
                  {answerLabel}
                </LmsButton>
              ))}
            </div>
          </div>
          {matchAssignments.length === answerReferenceLabels.length && !selectedResult ? (
            <LmsButton
              onClick={() => {
                applySelfStudyResult(
                  matchAssignments.every((answer, index) => answer === answerReferenceLabels[index])
                    ? 'correct'
                    : 'incorrect',
                )
              }}
              variant="accent"
            >
              Check matches
            </LmsButton>
          ) : null}
        </div>
      ) : null}

      <hr className="selfStudyDivider" />

      {isExamplesTemplate ? (
        <LmsButton
          disabled={continuePending}
          isLoading={continuePending}
          loadingLabel={continuePendingLabel}
          onClick={() => {
            onContinue('correct')
          }}
          size="lg"
          variant="accent"
        >
          Continue
        </LmsButton>
      ) : selectedResult ? (
        <TemplateContinueActions
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          incorrectReviewText={incorrectReviewText}
          isMobileOrTablet={isMobileOrTablet}
          onContinue={onContinue}
          result={selectedResult}
        />
      ) : showAnswerReference || answers.length === 0 ? (
        <div className="selfStudyStack selfStudyStack--responsive-row-1-5">
          <LmsButton
            onClick={() => {
              applySelfStudyResult('correct')
            }}
            size="lg"
            variant="accent"
          >
            I got it
          </LmsButton>
          <LmsButton
            onClick={() => {
              applySelfStudyResult('incorrect')
            }}
            size="lg"
            variant="outline"
          >
            Repeat later
          </LmsButton>
        </div>
      ) : null}
    </div>
  )
}

function LegacyTemplateContent({
  answerReferenceLabels,
  answers,
  continuePending,
  continuePendingLabel,
  incorrectReviewText,
  isMobileOrTablet,
  item,
  locale,
  onContinue,
}: {
  readonly answerReferenceLabels: string[]
  readonly answers: Array<LearningAnswerOption | string>
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly incorrectReviewText: string | null
  readonly isMobileOrTablet: boolean
  readonly item: LearningContentItem
  readonly locale: string
  readonly onContinue: (result: SelfStudyContinueResult) => void
}) {
  const templateKey = String(item.template ?? '')
  const template = resolveLegacySelfStudyTemplate(item)
  const templateContract = getLegacySelfStudyTemplateContract(template)
  const sourceOwner = templateContract?.sourceOwner ?? `Template${template ?? 'Unknown'}.js`
  const incorrectReview = resolveLegacyCorrectAnswerReviewText(
    item,
    answers,
    locale,
    template,
    answerReferenceLabels,
  )

  if (!template) {
    return (
      <div className="selfStudyStepCard selfStudyWarning" role="alert">
        This workbook step uses an unsupported legacy template ({templateKey || 'unknown'}).
      </div>
    )
  }


  if (template === '7') {
    return (
      <>
        <LegacyQuestionContent
          includeQuestionListFallback={false}
          item={item}
          locale={locale}
        />
        <LegacySpeechAnswerTemplate
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          expectedAnswers={answerReferenceLabels}
          incorrectReviewText={incorrectReview ?? incorrectReviewText}
          isMobileOrTablet={isMobileOrTablet}
          onContinue={onContinue}
          sourceOwner={sourceOwner}
        />
      </>
    )
  }

  if (template === '11') {
    return (
      <ConversationTemplate
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        item={item}
        locale={locale}
        onContinue={onContinue}
        onResult={playLearningFeedbackAudio}
        sourceOwner={sourceOwner}
      />
    )
  }

  if (
    template === '1' ||
    template === '2' ||
    template === '5' ||
    template === '6' ||
    template === '10'
  ) {
    return (
      <NumericTemplateContent
        answerReferenceLabels={answerReferenceLabels}
        answers={answers}
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        incorrectReviewText={incorrectReviewText}
        item={item}
        key={item.id}
        isMobileOrTablet={isMobileOrTablet}
        locale={locale}
        onContinue={onContinue}
        template={template}
      />
    )
  }

  const registeredTemplate = renderRegisteredSelfStudyTemplate({
    continuePending,
    continuePendingLabel,
    expectedAnswers: answerReferenceLabels,
    incorrectReviewText: incorrectReview ?? incorrectReviewText,
    isMobileOrTablet,
    item,
    locale,
    onContinue,
    onResult: playLearningFeedbackAudio,
    questionContent: (
      <LegacyQuestionContent
        includeQuestionListFallback={false}
        item={item}
        locale={locale}
      />
    ),
    sourceOwner,
    template,
  })

  if (registeredTemplate) {
    return registeredTemplate
  }

  if (template === '13') {
    return (
      <div
        className="legacySelfStudyTemplate13Details"
        data-legacy-owner={sourceOwner}
        data-legacy-template="13"
        data-qa="legacy-template-13-details"
      >
        <LegacyQuestionContent
          includeQuestionListFallback={false}
          item={item}
          locale={locale}
        />
        <SpeechPracticeTemplate
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          expectedAnswers={answerReferenceLabels}
          isMobileBlocked={isMobileOrTablet}
          mode="self-check"
          onContinue={onContinue}
          sourceOwner={sourceOwner}
        />
      </div>
    )
  }

  return (
    <SparseTemplateFallback
      answerReferenceLabels={answerReferenceLabels}
      answers={answers}
      continuePending={continuePending}
      continuePendingLabel={continuePendingLabel}
      incorrectReviewText={incorrectReviewText}
      isMobileOrTablet={isMobileOrTablet}
      item={item}
      locale={locale}
      onContinue={onContinue}
      template={template}
    />
  )
}

export function SelfStudyStepRenderer({
  chapter,
  continuePending,
  continuePendingLabel,
  elapsedSeconds,
  onContinue,
  onNext,
  slide,
}: {
  readonly chapter: LearningWorkbookChapter
  readonly continuePending: boolean
  readonly continuePendingLabel: ReactNode
  readonly elapsedSeconds: number
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly onNext: () => void
  readonly slide: SelfStudySlide
}) {
  useWarmLearningFeedbackAudio()
  const { state } = useDashboardState()
  const { i18n, t } = useTranslation('book')
  const locale = resolveReaderLanguage(i18n.language)
  const isMobileOrTablet = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false
    }

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  }, [])
  const introduction = useMemo(
    () => resolveLocalizedMapText(chapter.introduction, locale),
    [chapter.introduction, locale],
  )

  const implementation = useMemo(
    () => resolveLocalizedMapText(chapter.implementation, locale),
    [chapter.implementation, locale],
  )

  if (slide.kind === 'introduction') {
    return (
      <div
        className="selfStudyStepRenderer legacySelfStudyTemplateWrapper legacySelfStudyIntroStep"
        data-legacy-owner="TemplateIntroduction.js -> BoxTemplateWrapper.js -> BoxResult.js"
        data-legacy-template="introduction"
        data-qa="legacy-template-introduction"
      >
        <LearningTemplateIntroduction
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          contentHtml={introduction}
          imageSrc="/images/learning-introduce.webp"
          onContinue={onNext}
        />
      </div>
    )
  }

  if (slide.kind === 'implementation') {
    return (
      <div
        className="selfStudyStepRenderer legacySelfStudyTemplateWrapper legacySelfStudyImplementationStep"
        data-legacy-owner="TemplateImplementation.js -> Confetti -> BoxResult.js"
        data-legacy-template="implementation"
        data-qa="legacy-template-implementation"
      >
        <LegacySelfStudyCompletionConfetti />
        <div className="boxWrapQuestion legacySelfStudyQuestionWrap">
          <div className="legacySelfStudyTemplateIcon finish">
            <img alt="Learning complete" src="/images/learning-done.webp" />
          </div>
          <div className="legacySelfStudyCompletionLabel">
            <Timer aria-hidden="true" />
            <span>{t('Your completion time')}</span>
          </div>
          <div className="legacySelfStudyCompletionDuration">
            {formatElapsedDuration(elapsedSeconds)}
          </div>
          {implementation ? (
            <div className="legacySelfStudyTemplateQuestion">
              <HtmlBlock html={implementation} />
            </div>
          ) : null}
        </div>
        <LegacyTemplateResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          finished
          onNext={onNext}
        />
      </div>
    )
  }

  const item = selectPreferredLearningAudio(slide.item, state.myUser?.accent_default)
  const templateKey = String(item.template ?? 'unknown')
  const note = resolveLocalizedMapText(item.note, locale)
  const answers = normalizeAnswerOptions(item.answers)
  const answerReferenceLabels = answers
    .map((answer) => resolveLegacyAnswerOptionPayloadLabel(answer, locale, {
      suppressGenericPlaceholder: true,
    }))
    .filter(Boolean)
  const incorrectReviewText =
    note ?? (answerReferenceLabels.length > 0 ? answerReferenceLabels.join(' / ') : null)

  return (
    <div className={`selfStudyStack selfStudyStack--3 selfStudyStepRenderer legacySelfStudyContentStep template-${templateKey}`}>
      <LegacyTemplateContent
        answerReferenceLabels={answerReferenceLabels}
        answers={answers}
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        incorrectReviewText={incorrectReviewText}
        isMobileOrTablet={isMobileOrTablet}
        item={item}
        locale={locale}
        onContinue={onContinue}
      />
    </div>
  )
}
